// packages/ui/src/components/Layout/AppShell.tsx
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react'

import { IconMenu2 } from '../../icons'
import { cn } from '../../utils/cn'
import { AmbientOrbs } from '../AmbientOrbs'
import { AppIcon } from '../AppIcon'
import { LoadingSpinner } from '../common/LoadingSpinner'

export type AppShellBreakpoint = 'lg' | 'xl'

/** Classes Tailwind complètes (JIT-safe) selon le breakpoint desktop. */
const BREAKPOINT_CLASSES: Record<AppShellBreakpoint, { asideHidden: string; mobileOnly: string }> =
  {
    lg: { asideHidden: 'max-lg:hidden', mobileOnly: 'lg:hidden' },
    xl: { asideHidden: 'max-xl:hidden', mobileOnly: 'xl:hidden' },
  }

export interface AppShellProps {
  /** Variante des ambient orbs de fond. */
  ambientVariant?: 'default' | 'academy' | 'prompts'
  /** Sidebar rendue côté desktop et dans l'overlay mobile. */
  sidebar: ReactNode
  /** Largeur de la sidebar desktop (classe Tailwind littérale, ex. 'w-[340px]'). */
  sidebarWidthClass?: string
  /** Breakpoint à partir duquel la sidebar desktop est visible (lg | xl). */
  desktopBreakpoint?: AppShellBreakpoint
  /** Classes additionnelles pour la topbar (hauteur, fond...). */
  headerClassName?: string
  /** Classes additionnelles pour le conteneur de contenu (padding, max-width...). */
  contentClassName?: string
  /** Slot central de la topbar (breadcrumb, spacer...). Par défaut : spacer flex-1. */
  headerCenter?: ReactNode
  /** Actions à droite de la topbar (switcher, thème, user...). */
  headerActions?: ReactNode
  /** Overlays additionnels rendus hors flux (onboarding, raccourcis...). */
  overlays?: ReactNode
  /**
   * Clé de route courante (ex. pathname). Au changement : reset du scroll +
   * fermeture du menu mobile. Évite une dépendance à react-router dans le
   * design system.
   */
  routeKey?: string
  /** Contenu principal scrollable (généralement <Outlet />). */
  children: ReactNode
}

/**
 * Coquille d'application partagée NeuroForge — source unique du layout.
 *
 * Centralise toute la structure commune Academy / Prompts : fond + ambient
 * orbs, sidebar desktop, overlay mobile, topbar, zone scrollable avec
 * Suspense, et reset du scroll au changement de route.
 *
 * Les différences entre apps passent uniquement par les props/slots :
 * `ambientVariant`, `sidebarWidthClass`, `desktopBreakpoint`,
 * `headerClassName`, `headerCenter`, `headerActions`, `overlays`.
 */
export function AppShell({
  ambientVariant = 'default',
  sidebar,
  sidebarWidthClass = 'w-[340px]',
  desktopBreakpoint = 'lg',
  headerClassName,
  contentClassName,
  headerCenter,
  headerActions,
  overlays,
  routeKey,
  children,
}: AppShellProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Reset scroll + fermeture du menu mobile à chaque navigation.
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0)
    setMobileMenuOpen(false)
  }, [routeKey])

  // Échap ferme l'overlay mobile (comportement commun aux deux apps).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const bp = BREAKPOINT_CLASSES[desktopBreakpoint]

  return (
    <div className="duration-250 text-(--text) relative h-screen overflow-hidden transition-colors">
      {/* Ambient orbs (template body::before / body::after) */}
      <AmbientOrbs variant={ambientVariant} />

      {/* App shell — fullscreen flex like template .app-shell */}
      <div className="relative z-[1] flex h-full">
        {/* ─── Sidebar (desktop) ─── */}
        {/* `relative z-40` : la sidebar héberge désormais des dropdowns
            (AppSwitcher, filtres). backdrop-blur crée un contexte d'empilement,
            donc sans z-index explicite ses enfants absolus passeraient sous la
            topbar (z-30), quel que soit leur propre z-index. */}
        <aside
          className={cn(
            'border-(--border) dark:from-slate-900/78 dark:to-slate-900/36 relative z-40 flex h-full shrink-0 flex-col border-r backdrop-blur-[20px]',
            sidebarWidthClass,
            bp.asideHidden,
          )}
        >
          {sidebar}
        </aside>

        {/* ─── Main shell ─── */}
        <main className="flex min-w-0 flex-1 flex-col">
          {/* Topbar */}
          <header
            className={cn(
              'border-(--border) z-30 flex h-16 items-center justify-between border-b px-5 backdrop-blur-[18px] md:px-8',
              headerClassName,
            )}
          >
            {/* Bouton menu (mobile) */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className={cn(
                'glass text-(--muted) mr-4 flex h-10 w-10 items-center justify-center rounded-xl',
                bp.mobileOnly,
              )}
              aria-label="Ouvrir le menu"
            >
              <AppIcon icon={IconMenu2} size={20} aria-hidden />
            </button>

            {/* Slot central (breadcrumb / spacer) */}
            {headerCenter ?? <div className="min-w-0 flex-1" />}

            {/* Actions à droite */}
            <div className="flex shrink-0 items-center gap-3">{headerActions}</div>
          </header>

          {/* Zone scrollable */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            <Suspense
              fallback={
                <div className="flex justify-center py-20">
                  <LoadingSpinner size="lg" />
                </div>
              }
            >
              <div
                className={cn(
                  'mx-auto w-full max-w-7xl px-5 py-6 md:px-8 lg:px-10',
                  contentClassName,
                )}
              >
                {children}
              </div>
            </Suspense>
          </div>
        </main>
      </div>

      {/* Overlays spécifiques à l'app (onboarding, raccourcis...) */}
      {overlays}

      {/* ─── Overlay sidebar (mobile) ─── */}
      {mobileMenuOpen && (
        <div
          className={cn('fixed inset-0 z-50 bg-black/40 backdrop-blur-sm', bp.mobileOnly)}
          onClick={() => setMobileMenuOpen(false)}
        >
          <aside
            className="border-(--border) h-full w-[320px] overflow-y-auto border-r bg-[var(--bg)]"
            onClick={(e) => e.stopPropagation()}
          >
            {sidebar}
          </aside>
        </div>
      )}
    </div>
  )
}
